import { useCallback, useMemo } from "react";
import type { InputErrorType } from "@b2bportal/core-types";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { MonthAndDatePickerType } from "@tenant/ui";
import dayjs from "dayjs";

export interface DateRangePickerComponentProps {
  startDate: string | undefined;
  endDate: string | undefined;
  onStartDateChangeHandler: (date: string | undefined) => void;
  onEndDateChangeHandler: (date: string | undefined) => void;
  onDoneClick?: () => void;
  onClose?: () => void;
  dialogTitle?: string;
  classNames?: {
    content?: string;
  };
  triggerButton?: JSX.Element;
  error?: InputErrorType;
}

export const useDateRangePicker = ({
  startDate: stringStartDate,
  endDate: stringEndDate,
  onStartDateChangeHandler,
  onEndDateChangeHandler,
  onDoneClick,
  onClose,
  dialogTitle,
  error,
}: DateRangePickerComponentProps) => {
  const { t } = useI18nContext();

  const { isDesktopAndUp } = useDeviceType();

  const startDate = useMemo(() => {
    return stringStartDate != null ? dayjs(stringStartDate).toDate() : null;
  }, [stringStartDate]);

  const endDate = useMemo(() => {
    return stringEndDate != null ? dayjs(stringEndDate).toDate() : null;
  }, [stringEndDate]);

  const onStartDateChange = useCallback(
    (date: Date | null) => {
      onStartDateChangeHandler(date ? date.toString() : undefined);
    },
    [onStartDateChangeHandler]
  );

  const onEndDateChange = useCallback(
    (date: Date | null) => {
      onEndDateChangeHandler(date ? date.toString() : undefined);
    },
    [onEndDateChangeHandler]
  );

  const onOpen = useCallback(() => {
    // Activities-specific tracking can be added here
  }, []);

  const onDone = useCallback(() => {
    onDoneClick?.();
  }, [onDoneClick]);

  return {
    context: {
      stringStartDate,
      stringEndDate,
      startDate,
      endDate,
      dialogFullscreen: !isDesktopAndUp,
      calendarViewType: isDesktopAndUp
        ? MonthAndDatePickerType.Horizontal
        : MonthAndDatePickerType.Column,
      error,
    },
    handlers: {
      onDoneClick: onDone,
      onStartDateChange,
      onEndDateChange,
      onClose,
      onOpen,
    },
    text: {
      selectDatesLabel: t("core-activities.search.date.whenLabel"),
      dialogDone: t("core-activities.search.date.applyButton"),
      dialogTitle,
      placeholder: t("core-activities.search.date.selectDates"),
    },
  };
};
